import type { LiveProject, ProjectsById } from '@wiztime/core';
import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { settingsPath, SettingsSection } from '../../../app/routes';
import { cx } from '../../../lib/cx';
import { projectHex } from '../../../lib/projectColor';
import { CheckIcon, ChevronDownIcon } from '../../../ui/icons';
import { flatGroups, groupRows, toggled, type ProjectGroup } from '../projectGroups';
import styles from './ProjectPicker.module.css';

/** Number keys 1 to 9 choose a project (settings order, 2026-10b §4.8). */
export const SHORTCUT_COUNT = 9;

/** "1 2 … 9" for aria-keyshortcuts of the button. */
const DIGIT_SHORTCUTS = Array.from({ length: SHORTCUT_COUNT }, (_, index) => String(index + 1)).join(' ');

/**
 * The digit shortcut of the picker: the key 1–9 picks the n-th project of the settings order,
 * the same numbering as the quick-mark tiles (§4.8) — not the n-th row of the recent-use
 * display order. Returns true when the event was a digit that was handled. Callers attach it
 * to exactly one wrapper element, so a key press never chooses twice.
 */
export function chooseByDigit(
  event: KeyboardEvent<HTMLElement>,
  digitProjects: readonly LiveProject[],
  onChoose: (projectId: string) => void,
): boolean {
  if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return false;
  if (!/^[1-9]$/.test(event.key)) return false;
  // Typing a number into a field (time, title, note) stays typing.
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return false;
  const project = digitProjects[Number(event.key) - 1];
  if (project === undefined) return false;
  event.preventDefault();
  onChoose(project.id);
  return true;
}

interface ProjectDropdownProps {
  /** Active projects, recent use first. */
  readonly projects: readonly LiveProject[];
  /** `projects` grouped under their parents (project hierarchy); omitted: shown flat. */
  readonly groups?: readonly ProjectGroup[];
  /** Every project, to resolve a child project's inherited colour. */
  readonly projectsById?: ProjectsById;
  /** Top-level projects in settings order: the digit keys pick from this list. */
  readonly digitProjects: readonly LiveProject[];
  /** The block's current project (may be deactivated or gone, hence not taken from `projects`). */
  readonly current: LiveProject | null;
  readonly onChoose: (projectId: string) => void;
}

/**
 * "Change project" of an existing block (2026-10c §3): a button showing the current project
 * (colour dot, name, chevron) that opens a listbox right below it — at most about 240px high,
 * scrolling, the current project checked, recent use first, the digit shortcut at each row's
 * end and a permanent "Manage projects" link at the bottom. The digit keys work with the list
 * closed and open (one handler on the wrapper). Esc closes only the list and returns focus to
 * the button.
 */
export function ProjectDropdown({
  projects,
  groups: groupsProp,
  projectsById,
  digitProjects,
  current,
  onChoose,
}: ProjectDropdownProps) {
  const { t } = useTranslation();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const digitIndex = new Map(digitProjects.map((project, index) => [project.id, index]));
  const byId = projectsById ?? new Map<string, LiveProject>();
  const groups = groupsProp ?? flatGroups(projects);
  const [earlierOpen, setEarlierOpen] = useState<ReadonlySet<string>>(new Set());
  // All groups are expanded here (a dropdown is for picking, not scanning); the folded children
  // stay under "earlier", except in the group of the block's own project.
  const allIds = new Set(groups.map((group) => group.project.id));
  const forceOpen = new Set(
    groups.filter((group) => group.folded.some((child) => child.id === current?.id)).map((group) => group.project.id),
  );
  const rows = groupRows(groups, { expanded: allIds, earlierOpen }, forceOpen);

  useEffect(() => {
    if (!open) return;
    const options = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);
    (options.find((option) => option.getAttribute('aria-selected') === 'true') ?? options[0])?.focus({
      preventScroll: false,
    });
  }, [open]);

  const options = (): HTMLButtonElement[] =>
    Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (chooseByDigit(event, digitProjects, onChoose)) return;
    // Alt, Ctrl and Meta combinations (Alt+arrows move the block) belong to the page.
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus({ preventScroll: true });
      return;
    }
    if (event.defaultPrevented) return;
    if (event.target === buttonRef.current && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    const buttons = options();
    const index = buttons.findIndex((button) => button === event.target);
    if (index === -1) return;
    const target =
      event.key === 'ArrowDown'
        ? index + 1
        : event.key === 'ArrowUp'
          ? index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? buttons.length - 1
              : null;
    if (target === null) return;
    event.preventDefault();
    buttons[Math.min(buttons.length - 1, Math.max(0, target))]?.focus();
  };

  return (
    <div
      ref={wrapRef}
      className={styles.changeWrap}
      data-testid="picker-change-wrap"
      onKeyDown={onKeyDown}
      onBlur={(event) => {
        // Focus leaving the control (Tab, a click elsewhere) folds the list.
        if (open && !(event.relatedTarget instanceof Node && wrapRef.current?.contains(event.relatedTarget))) {
          setOpen(false);
        }
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        className={cx(styles.changeButton, open && styles.changeButtonOpen)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open && projects.length > 0 ? listId : undefined}
        aria-keyshortcuts={DIGIT_SHORTCUTS}
        data-testid="picker-change-project"
        onClick={() => setOpen((value) => !value)}
      >
        <span
          className={styles.dot}
          style={{ '--dot-color': current === null ? 'var(--border-control)' : projectHex(current, byId) } as CSSProperties}
        />
        <span className={styles.projectName} data-testid="picker-current-project">
          {current === null ? t('timeline.unknownProject') : current.data.name}
        </span>
        <span className={styles.changeLabel}>{t('picker.changeProject')}</span>
        <ChevronDownIcon />
      </button>
      {open ? (
        <div className={styles.dropdown} data-testid="picker-dropdown">
          {projects.length === 0 ? (
            <p className={styles.dropdownEmpty}>{t('picker.empty')}</p>
          ) : (
            <ul
              id={listId}
              ref={listRef}
              role="listbox"
              className={styles.dropdownList}
              aria-label={t('picker.projects')}
            >
              {rows.map((row) => {
                if (row.kind === 'earlier') {
                  const parentId = row.group.project.id;
                  return (
                    <li key={`earlier-${parentId}`} role="presentation">
                      <button
                        type="button"
                        className={cx(styles.option, styles.optionEarlier)}
                        aria-expanded={row.open}
                        data-testid="picker-earlier"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => setEarlierOpen((previous) => toggled(previous, parentId))}
                      >
                        {t('picker.earlier', { count: row.group.folded.length })}
                      </button>
                    </li>
                  );
                }
                const { project, level } = row;
                const digit = level === 0 ? (digitIndex.get(project.id) ?? SHORTCUT_COUNT) : SHORTCUT_COUNT;
                const isCurrent = project.id === current?.id;
                return (
                  <li key={project.id} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isCurrent}
                      aria-keyshortcuts={digit < SHORTCUT_COUNT ? String(digit + 1) : undefined}
                      className={cx(styles.option, level === 1 && styles.optionChild, isCurrent && styles.optionCurrent)}
                      title={project.data.name}
                      data-testid="picker-option"
                      data-level={level}
                      // Keep focus where it is: a blur here would fold the list before the click lands.
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => onChoose(project.id)}
                    >
                      <span
                        className={styles.dot}
                        style={{ '--dot-color': projectHex(project, byId) } as CSSProperties}
                      />
                      <span className={styles.projectName}>{project.data.name}</span>
                      {isCurrent ? (
                        <span className={styles.optionCheck} aria-hidden="true">
                          <CheckIcon />
                        </span>
                      ) : null}
                      {digit < SHORTCUT_COUNT ? (
                        <span className={styles.shortcut} aria-hidden="true" data-testid="picker-shortcut">
                          {digit + 1}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <ManageProjectsLink />
        </div>
      ) : null}
    </div>
  );
}

/** The permanent text link to Settings → Projects at the bottom of the project list or dropdown. */
export function ManageProjectsLink() {
  const { t } = useTranslation();
  return (
    <Link className={styles.manageLink} to={settingsPath(SettingsSection.Projects)} data-testid="picker-manage-projects">
      {t('picker.manageProjects')}
    </Link>
  );
}
